import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CURRENCY } from '@78/i18n';
import { useTranslation } from 'react-i18next';
import { useTonAddress } from '@tonconnect/ui-react';
import { fetchReferralMe } from '../lib/api.ts';
import { invitationLink } from '../lib/referral.ts';
import { getInitData, getReferralCode, openExternal } from '../lib/telegram.ts';
import { useConfirmWallet } from '../lib/confirm.ts';
import { useErrorText } from '../lib/errors.ts';
import { useFmt } from '../lib/format.tsx';

/**
 * Приглашения.
 *
 * Экран про деньги, поэтому первым идёт не призыв, а устройство: доля
 * отделяется от нашей комиссии, а не добавляется к цене, и платит её
 * контракт. Человек, который приводит знакомых, должен понимать, откуда
 * берётся его доход, — иначе он не поверит, что она вообще придёт.
 */
export function Referral({ onProfile }: { onProfile: () => void }) {
  const { t, i18n } = useTranslation();
  const { money } = useFmt();
  const myAddress = useTonAddress(false);
  const errorText = useErrorText();
  const confirmWallet = useConfirmWallet(i18n.language === 'ru' ? 'ru' : 'en');
  const [copied, setCopied] = useState(false);

  const { data, error, refetch } = useQuery({
    queryKey: ['referral', myAddress],
    queryFn: () => fetchReferralMe({ initData: getInitData(), address: myAddress }),
    enabled: myAddress.length > 0 && getInitData().length > 0,
    retry: false,
  });

  const link = data === undefined ? null : invitationLink(data.bot, data.code);

  const copy = () => {
    if (link === null) return;
    void navigator.clipboard
      ?.writeText(link)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Буфер может быть закрыт разрешениями — код виден на экране и
        // переписывается руками, поэтому молча.
      });
  };

  const share = () => {
    if (link === null) return;
    openExternal(`https://t.me/share/url?url=${encodeURIComponent(link)}`);
  };

  return (
    <div className="screen">
      <div className="col" style={{ gap: 2 }}>
        <strong style={{ fontSize: 17 }}>{t('ref.title')}</strong>
        <span className="muted" style={{ fontSize: 13 }}>{t('ref.lead')}</span>
      </div>

      {/* Пришёл по чужой ссылке и ещё не покупал — стоит сказать прямо:
          приглашение не пропало, оно ждёт первой покупки. */}
      {getReferralCode() !== '' && (
        <div className="card text" style={{ background: 'var(--lime-soft)', borderColor: 'transparent' }}>
          {t('ref.invited_by')}
        </div>
      )}

      {myAddress.length === 0 || getInitData().length === 0 ? (
        <div className="card text">{t('ref.need_wallet')}</div>
      ) : error !== null && error !== undefined ? (
        /* Причину называем: «подключите кошелёк» человеку с подключённым
           кошельком ничего не объясняет, а чаще всего дело именно в
           неподтверждённом — подпись он ещё не ставил. */
        <div className="card col" style={{ gap: 6 }}>
          <span className="text">
            {String((error as Error).message) === 'wallet_not_confirmed'
              ? t('ref.not_confirmed')
              : t('ref.failed')}
          </span>
          <span className="dim" style={{ fontSize: 11 }}>{errorText(error)}</span>
          {String((error as Error).message) === 'wallet_not_confirmed' && (
            <>
              {/* Подтверждаем здесь же: кнопка, которая только уводит в
                  профиль, называлась «подтвердить» и ничего не подтверждала
                  — человек нажимал её и возвращался ни с чем. */}
              <button
                className="btn btn-sm"
                style={{ width: 'auto' }}
                disabled={confirmWallet.busy}
                onClick={() => {
                  void confirmWallet.confirm().then((ok) => {
                    if (ok) void refetch();
                  });
                }}
              >
                {confirmWallet.busy ? '…' : t('ref.go_confirm')}
              </button>
              {confirmWallet.error !== null && (
                <span className="danger" style={{ fontSize: 12 }}>{confirmWallet.error}</span>
              )}
              <button className="btn btn-ghost btn-sm" style={{ width: 'auto' }} onClick={onProfile}>
                {t('ref.open_profile')}
              </button>
            </>
          )}
        </div>
      ) : data === undefined ? (
        <div className="skeleton" style={{ minHeight: 140 }} />
      ) : !data.enabled ? (
        <div className="card text">{t('ref.disabled')}</div>
      ) : (
        <>
          <div className="card col" style={{ gap: 8 }}>
            <div className="label">{t('ref.your_link')}</div>
            <div
              className="stat-value"
              style={{ wordBreak: 'break-all', lineHeight: 1.35 }}
            >
              {link ?? `${t('ref.code')}: ${data.code}`}
            </div>
            <span className="muted" style={{ fontSize: 12 }}>{t('ref.link_opens')}</span>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-sm" onClick={copy} disabled={link === null}>
                {copied ? t('ref.copied') : t('ref.copy')}
              </button>
              <button className="btn btn-sm btn-ghost" onClick={share} disabled={link === null}>
                {t('ref.share')}
              </button>
            </div>
          </div>

          <div className="stats2">
            <div className="stat">
              <div className="label">{t('ref.visits')}</div>
              <div className="stat-value">{data.visits}</div>
            </div>
            <div className="stat">
              <div className="label">{t('ref.invited')}</div>
              <div className="stat-value">{data.invited}</div>
            </div>
            <div className="stat">
              <div className="label">{t('ref.deals')}</div>
              <div className="stat-value">{data.deals}</div>
            </div>
            <div className="stat">
              <div className="label">{t('ref.paid', { cur: CURRENCY })}</div>
              <div className="stat-value">{money(data.paid)}</div>
            </div>
            <div className="stat">
              <div className="label">{t('ref.pending', { cur: CURRENCY })}</div>
              <div className="stat-value">{money(data.pending)}</div>
            </div>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>{t('ref.visits_note')}</span>
          <span className="muted" style={{ fontSize: 12 }}>{t('ref.pending_note')}</span>
        </>
      )}

      <div className="label">{t('ref.how_title')}</div>
      {['ref.how_1', 'ref.how_2', 'ref.how_3'].map((key, i) => (
        <div key={key} className="card row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <span className="stat-value" style={{ opacity: 0.5 }}>{i + 1}</span>
          <span className="text">{t(key)}</span>
        </div>
      ))}

      <div className="card text">{t('ref.onchain_note')}</div>
      <div className="card text">{t('ref.self_note')}</div>
    </div>
  );
}
